"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, Clock, ShieldCheck, Zap, CircleAlert as AlertCircle, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const guarantees = [{
  id: 1,
  title: "Fast Local Response",
  detail: "Units on standby across Jonesboro & Clayton County"
}, {
  id: 2,
  title: "Upfront Pricing",
  detail: "Clear flat rates before any repair begins"
}, {
  id: 3,
  title: "Experienced Plumbers",
  detail: "Fully insured and equipped for immediate repairs"
}];
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencycalltoaction" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-y border-border relative overflow-hidden">
      {/* Background Accent Gradients */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="bg-card text-card-foreground border border-border shadow-xl rounded-2xl p-6 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="destructive" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse mr-1.5" />
                  24/7 Rapid Dispatch
                </Badge>
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-primary" /> Average arrival: 30-45 mins
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default leading-tight">
                Plumbing Emergency in Jonesboro? We Are Standing By.
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground font-default max-w-2xl">
                Burst pipes, major sewage backups, or a failed water heater cannot wait. Our licensed emergency plumbers are on the road 24 hours a day, 7 days a week to protect your home.
              </p>

              {/* Guarantees List */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {guarantees.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col gap-1 p-3 rounded-lg bg-secondary/50 border border-border/60">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                      {item.id === 1 && <Zap className="w-3.5 h-3.5 text-accent" />}
                      {item.id === 2 && <ShieldCheck className="w-3.5 h-3.5 text-primary" />}
                      {item.id === 3 && <AlertCircle className="w-3.5 h-3.5 text-primary" />}
                      <span>{item.title}</span>
                    </div>
                    <span className="text-xs text-muted-foreground leading-snug">
                      {item.detail}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Right Action Card */}
            <div className="lg:col-span-5 flex flex-col items-stretch gap-4 bg-background p-6 sm:p-8 rounded-xl border border-border shadow-md">
              <div className="text-center space-y-1">
                <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Immediate Phone Support
                </span>
                <p className="text-sm text-foreground font-medium">
                  Direct line to on-call master plumbers
                </p>
              </div>

              {/* Primary Call Action */}
              <a href="tel:4708479076" className="w-full">
                <Button size="lg" className="w-full h-14 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md flex items-center justify-center gap-3 transition-transform active:scale-98">
                  <PhoneCall className="w-6 h-6 animate-bounce" />
                  <span>{"(470) 847-9076"}</span>
                </Button>
              </a>

              <div className="relative flex items-center justify-center my-1">
                <div className="border-t border-border w-full" />
                <span className="bg-background px-3 text-xs text-muted-foreground uppercase">
                  or
                </span>
                <div className="border-t border-border w-full" />
              </div>

              {/* Secondary Online Request Action */}
              <Button variant="outline" size="lg" asChild className="w-full h-12 text-sm font-semibold border-border hover:bg-secondary text-foreground">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Submit Emergency Service Request</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>

              <p className="text-xs text-center text-muted-foreground pt-1">
                No extra charges for weekends or late night emergency calls in Jonesboro.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}